/**
 * CollectContact — Public Contact Collection Form
 *
 * A beautiful, inviting contact collection form for public signup.
 * Features:
 * - Contact fields (name, email, phone, address)
 * - Tenant's custom fields (dynamic)
 * - Granular consent capture (SMS/Email/Voice × Informational/Marketing)
 * - Honeypot spam protection
 * - Success animation
 * - Mobile-first responsive design
 *
 * Aesthetic: "Welcoming Portal" — soft gradients, floating card,
 * generous spacing, inviting micro-interactions, trust indicators.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCountriesWithPriority, getCountryByCode, executeRecaptcha } from '@respectly/utils';
import { isLayoutFieldType } from '@respectly/shared-types';
import { PhoneCountrySelect } from '../components/PhoneCountrySelect';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  Send,
  User,
  Mail,
  Phone,
  MapPin,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
// PublicFormConfig type - matches the schema in contact-collection.types.ts
interface PublicFormConfig {
  name: string;
  headerText?: string | null;
  welcomeMessage?: string | null;
  successMessage?: string | null;
  logoUrl?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  primaryColor?: string | null;
  showSmsConsent: boolean;
  showEmailConsent: boolean;
  showVoiceConsent: boolean;
  smsShowInformational: boolean;
  smsShowMarketing: boolean;
  emailShowInformational: boolean;
  emailShowMarketing: boolean;
  voiceShowInformational: boolean;
  voiceShowMarketing: boolean;
  smsConsentText?: string | null;
  emailConsentText?: string | null;
  voiceConsentText?: string | null;
  // New fields for enhanced form
  fieldVisibility?: {
    lastName?: boolean;
    company?: boolean;
    email?: boolean;
    phone?: boolean;
    address?: boolean;
    city?: boolean;
    state?: boolean;
    zipCode?: boolean;
    country?: boolean;
  } | null;
  inputBorderRadius?: number | null;
  inputBorderColor?: string | null;
  inputBackgroundColor?: string | null;
  inputTextColor?: string | null;
  footerConfig?: {
    enabled?: boolean;
    text?: string;
    links?: Array<{ label: string; url: string }>;
  } | null;
  customFields?: Array<{
    fieldKey: string;
    name: string;
    fieldType: string;
    isRequired: boolean;
    options?: string[] | null;
    placeholder?: string | null;
    helpText?: string | null;
  }>;
  companyName?: string;
  companyAddress?: string;
}
import { PortalLayout } from '../layouts/PortalLayout';
import { NotFoundPage } from './NotFoundPage';
import { LoadingPage } from './LoadingPage';

// ─── Types ──────────────────────────────────────────────

interface ConsentState {
  sms: { informational: boolean; marketing: boolean };
  email: { informational: boolean; marketing: boolean };
  voice: { informational: boolean; marketing: boolean };
}

interface ContactFields {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface CustomFieldValue {
  [key: string]: string | string[] | boolean;
}

// ─── Constants ──────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const PROVINCE_OPTIONS: Record<string, string[]> = {
  CA: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan'],
  US: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'],
};

// ─── Animation Variants ─────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const successVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const checkmarkVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.6, delay: 0.2, ease: 'easeInOut' },
  },
};

// ─── Props ──────────────────────────────────────────────

interface CollectContactProps {
  slug: string;
}

// ─── Image Upload Field ─────────────────────────────────

interface ImageUploadFieldProps {
  fieldKey: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  error?: string;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function ImageUploadField({ fieldKey, value, placeholder, onChange, error }: ImageUploadFieldProps) {
  const inputId = `custom_${fieldKey}`;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setLocalError('Please select an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setLocalError('Image must be 5MB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        setLocalError(null);
        onChange(result);
      } else {
        setLocalError('Could not read the image file.');
      }
    };
    reader.onerror = () => setLocalError('Could not read the image file.');
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleClear = () => {
    setLocalError(null);
    onChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const borderClass = error || localError ? 'border-red-300' : isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/40';

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      {value ? (
        <div className="relative rounded-md border border-slate-200 bg-slate-50 overflow-hidden">
          <img src={value} alt="Uploaded preview" className="w-full max-h-64 object-contain" />
          <button
            type="button"
            onClick={handleClear}
            aria-label="Remove image"
            className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-900/60 text-white hover:bg-slate-900/80 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`w-full flex flex-col items-center justify-center gap-2 px-6 py-8 rounded-md border-2 border-dashed text-center transition-colors ${borderClass}`}
        >
          <Upload className="h-6 w-6 text-slate-500" />
          <p className="text-sm font-medium text-slate-700">
            {placeholder || 'Click to upload or drag and drop'}
          </p>
          <p className="text-xs text-slate-500">PNG, JPG, GIF up to 5MB</p>
        </button>
      )}
      {localError && (
        <p className="text-xs text-red-500">{localError}</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function CollectContact({ slug }: CollectContactProps) {
  // ─── Core State ─────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formConfig, setFormConfig] = useState<PublicFormConfig | null>(null);
  const [notFound, setNotFound] = useState(false);

  // ─── Contact Fields ────────────────────────────────
  const [contactFields, setContactFields] = useState<ContactFields>({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'CA',
  });

  // ─── Phone Dial Code ───────────────────────────────
  // Country ISO code selected for the phone prefix (separate from address country)
  const [phoneCountry, setPhoneCountry] = useState('CA');
  // Subscriber number typed by the user (without country code)
  const [phoneLocalNumber, setPhoneLocalNumber] = useState('');

  // Derive the dialing code prefix from the selected phone country
  const phoneDialCode = useMemo(() => {
    if (!phoneCountry) return '';
    const country = getCountryByCode(phoneCountry);
    if (!country?.phonecode) return '';
    const code = String(country.phonecode);
    return code.startsWith('+') ? code : `+${code}`;
  }, [phoneCountry]);

  // Countries list for the address country selector (memoized - doesn't change)
  const allCountries = useMemo(() => getCountriesWithPriority(), []);

  // ─── Custom Fields ─────────────────────────────────
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValue>({});

  // ─── Consent State ─────────────────────────────────
  const [consent, setConsent] = useState<ConsentState>({
    sms: { informational: false, marketing: false },
    email: { informational: false, marketing: false },
    voice: { informational: false, marketing: false },
  });
  // True once the visitor has touched a consent box. Distinguishes "hasn't got
  // there yet" (a first-time visitor — say nothing) from "deliberately cleared
  // every box" (a returning contact opting out — warn them what that means).
  const [consentTouched, setConsentTouched] = useState(false);

  // ─── Honeypot ──────────────────────────────────────
  const [honeypot, setHoneypot] = useState('');
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastSignaturePointRef = useRef<{ x: number; y: number } | null>(null);
  const [isDrawingSignature, setIsDrawingSignature] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);

  // ─── Validation Errors ─────────────────────────────
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ─── Submission State ──────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Signature prefilled from a prior submission — waits for the canvas to
  // initialize (tied to formConfig) before painting, then clears itself so
  // the user's subsequent clears/redraws work normally.
  const [pendingSignaturePrefill, setPendingSignaturePrefill] = useState<string | null>(null);

  // Prefill JWT from ?prefill=<token>. Read once on mount and forwarded on
  // submit so the backend can treat the submission as an authoritative
  // restatement of consent for the bound contact (unchecked = revoked).
  const [prefillToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('prefill');
  });

  // ─── Fetch Form Config ─────────────────────────────
  useEffect(() => {
    const fetchFormConfig = async () => {
      if (!slug) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/public/collect/${slug}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          if (response.status === 404) {
            setNotFound(true);
          } else {
            setError(result.error?.message || 'Failed to load form');
          }
          setIsLoading(false);
          return;
        }

        setFormConfig(result.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching form config:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };

    fetchFormConfig();
  }, [slug]);

  // ─── Prefill From Token ────────────────────────────
  // When the admin sends an intake link for an existing contact, the URL
  // includes `?prefill=<jwt>`. We exchange the token server-side for the
  // contact's current fields (including large custom-field values that
  // would blow out URL length limits) and populate the form once on mount.
  useEffect(() => {
    if (!prefillToken) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/intake-prefill/${encodeURIComponent(prefillToken)}`
        );
        if (!res.ok) return;
        const json = (await res.json()) as {
          success: boolean;
          data?: {
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            company: string;
            address: string;
            city: string;
            state: string;
            zipCode: string;
            country: string;
            customFields: Record<string, unknown>;
            consent?: {
              sms: { informational: boolean; marketing: boolean };
              email: { informational: boolean; marketing: boolean };
              voice: { informational: boolean; marketing: boolean };
            };
            signatureDataUrl?: string | null;
          };
        };
        if (cancelled || !json.success || !json.data) return;

        const d = json.data;
        setContactFields((prev) => ({
          ...prev,
          firstName: d.firstName || prev.firstName,
          lastName: d.lastName || prev.lastName,
          email: d.email || prev.email,
          company: d.company || prev.company,
          address: d.address || prev.address,
          city: d.city || prev.city,
          state: d.state || prev.state,
          zipCode: d.zipCode || prev.zipCode,
          country: d.country || prev.country,
        }));

        if (d.country) setPhoneCountry(d.country);

        // Split E.164 phone into the country+local combo the form uses.
        if (d.phone) {
          const match = d.phone.match(/^\+(\d{1,3})(\d+)$/);
          if (match) {
            const dialDigits = match[1]!;
            const local = match[2]!;
            const all = getCountriesWithPriority();
            const preferred = d.country
              ? all.find(
                  (c) =>
                    c.isoCode === d.country &&
                    String(c.phonecode).replace(/^\+/, '') === dialDigits
                )
              : undefined;
            const matchedCountry =
              preferred ??
              all.find((c) => String(c.phonecode).replace(/^\+/, '') === dialDigits);
            if (matchedCountry) {
              setPhoneCountry(matchedCountry.isoCode);
              setPhoneLocalNumber(local);
            } else {
              setPhoneLocalNumber(d.phone.replace(/^\+/, ''));
            }
          } else {
            setPhoneLocalNumber(d.phone);
          }
        }

        // Coerce each custom field value into the CustomFieldValue shape
        // (string | boolean | string[]) the form's renderers expect.
        const customPrefill: CustomFieldValue = {};
        for (const [fieldKey, value] of Object.entries(d.customFields)) {
          if (value === null || value === undefined || value === '') continue;
          if (typeof value === 'string' || typeof value === 'boolean') {
            customPrefill[fieldKey] = value;
          } else if (
            Array.isArray(value) &&
            value.every((v) => typeof v === 'string')
          ) {
            customPrefill[fieldKey] = value as string[];
          } else {
            customPrefill[fieldKey] = String(value);
          }
        }
        if (Object.keys(customPrefill).length > 0) {
          setCustomFieldValues((prev) => ({ ...prev, ...customPrefill }));
        }

        // Consent toggles — only pre-check when the contact has expressly
        // opted in on record. Never pre-check a purpose they previously
        // opted out of; the intake is their chance to re-affirm.
        if (d.consent) {
          setConsent({
            sms: { ...d.consent.sms },
            email: { ...d.consent.email },
            voice: { ...d.consent.voice },
          });
        }

        // Signature — stash the data URL; the canvas-prefill effect below
        // paints it once the canvas is initialized (which happens when
        // formConfig loads).
        if (d.signatureDataUrl) {
          setSignatureDataUrl(d.signatureDataUrl);
          setPendingSignaturePrefill(d.signatureDataUrl);
        }
      } catch (err) {
        console.error('[CollectContact] prefill fetch failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Handlers ──────────────────────────────────────

  const handleContactFieldChange = useCallback(
    (field: keyof ContactFields, value: string) => {
      setContactFields((prev) => ({ ...prev, [field]: value }));
      if (field === 'country') {
        setPhoneCountry(value || 'CA');
      }
      // Clear error when field changes
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [errors]
  );

  const handleCustomFieldChange = useCallback(
    (fieldKey: string, value: string | string[] | boolean) => {
      setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }));
      if (errors[`custom_${fieldKey}`]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[`custom_${fieldKey}`];
          return next;
        });
      }
    },
    [errors]
  );

  // Keep contactFields.phone in sync as E.164 whenever dial code or local number changes
  useEffect(() => {
    const digits = phoneLocalNumber.replace(/\D/g, '');
    const phone =
      phoneDialCode && digits
        ? `${phoneDialCode}${digits}`
        : phoneLocalNumber;
    setContactFields((prev) => ({ ...prev, phone }));
  }, [phoneDialCode, phoneLocalNumber]);

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = Math.max(1, window.devicePixelRatio || 1);
    const width = canvas.clientWidth || 600;
    const height = 140;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';
  }, [formConfig]);

  // Paint a prefilled signature onto the canvas once it's initialized.
  // Scales the image to fit the canvas box while preserving aspect ratio so
  // a wider signature from a previous device still reads correctly here.
  useEffect(() => {
    if (!pendingSignaturePrefill || !formConfig) return;
    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const img = new window.Image();
    img.onload = () => {
      const width = canvas.clientWidth || 600;
      const height = 140;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      const aspect = img.width / img.height || 1;
      let drawW = width;
      let drawH = width / aspect;
      if (drawH > height) {
        drawH = height;
        drawW = height * aspect;
      }
      const x = (width - drawW) / 2;
      const y = (height - drawH) / 2;
      ctx.drawImage(img, x, y, drawW, drawH);
      setPendingSignaturePrefill(null);
    };
    img.onerror = () => {
      setPendingSignaturePrefill(null);
    };
    img.src = pendingSignaturePrefill;
  }, [pendingSignaturePrefill, formConfig]);

  // Prevent page scroll while drawing on mobile (non-passive listeners required for preventDefault)
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const preventScroll = (e: TouchEvent) => e.preventDefault();

    canvas.addEventListener("touchstart", preventScroll, { passive: false });
    canvas.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", preventScroll);
      canvas.removeEventListener("touchmove", preventScroll);
    };
  }, []);

  const getSignaturePoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handleSignaturePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = signatureCanvasRef.current?.getContext('2d');
    const point = getSignaturePoint(event);
    if (!ctx || !point) return;

    event.preventDefault();
    setIsDrawingSignature(true);
    lastSignaturePointRef.current = point;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);

    if (errors.signature) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.signature;
        return next;
      });
    }
  };

  const handleSignaturePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingSignature) return;

    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    const point = getSignaturePoint(event);
    if (!ctx || !point) return;

    event.preventDefault();
    const lastPoint = lastSignaturePointRef.current;
    if (!lastPoint) {
      lastSignaturePointRef.current = point;
      return;
    }

    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastSignaturePointRef.current = point;
  };

  const finalizeSignature = () => {
    setIsDrawingSignature(false);
    lastSignaturePointRef.current = null;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    setSignatureDataUrl(canvas.toDataURL('image/png'));
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const width = canvas.clientWidth || 600;
    const height = 140;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    setSignatureDataUrl(null);
    setIsDrawingSignature(false);
    lastSignaturePointRef.current = null;
  };

  const handleConsentChange = useCallback(
    (channel: 'sms' | 'email' | 'voice', purpose: 'informational' | 'marketing', checked: boolean) => {
      setConsentTouched(true);
      setConsent((prev) => ({
        ...prev,
        [channel]: {
          ...prev[channel],
          [purpose]: checked,
        },
      }));
    },
    []
  );

  // ─── Validation ────────────────────────────────────

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Field visibility (default all to true if not specified)
    const fv = formConfig?.fieldVisibility || {};
    const emailVisible = fv.email !== false;
    const phoneVisible = fv.phone !== false;

    // First name required
    if (!contactFields.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Email or phone required - respecting field visibility
    if (emailVisible && phoneVisible) {
      // Both visible - require at least one
      if (!contactFields.email.trim() && !contactFields.phone.trim()) {
        newErrors.email = 'Email or phone is required';
      }
    } else if (emailVisible && !phoneVisible) {
      // Only email visible - make it required
      if (!contactFields.email.trim()) {
        newErrors.email = 'Email is required';
      }
    } else if (!emailVisible && phoneVisible) {
      // Only phone visible - make it required
      if (!contactFields.phone.trim()) {
        newErrors.phone = 'Phone is required';
      }
    }
    // If neither email nor phone visible, just first name required

    // Email format (only validate if field is visible and has value)
    if (emailVisible && contactFields.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactFields.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // Phone format (only validate if field is visible and has value)
    if (phoneVisible && contactFields.phone.trim()) {
      const phoneDigits = contactFields.phone.replace(/\D/g, '');
      if (phoneDigits.length < 7) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Custom field validation
    if (formConfig?.customFields) {
      for (const field of formConfig.customFields) {
        // Layout-only primitives (section_header, spacer) carry no value — skip.
        if (isLayoutFieldType(field.fieldType)) continue;
        if (field.isRequired) {
          const value = customFieldValues[field.fieldKey];
          if (!value || (typeof value === 'string' && !value.trim())) {
            newErrors[`custom_${field.fieldKey}`] = `${field.name} is required`;
          }
        }
      }
    }

    // NOTE: no client-side "at least one channel" gate. A submission with every
    // box unchecked is how a returning contact opts out of everything, and the
    // server is the only side that can tell the two cases apart: it revokes the
    // displayed cells for a matched contact, and rejects a brand-new lead with
    // "At least one communication preference is required" (surfaced inline).
    if (!signatureDataUrl) {
      newErrors.signature = 'Signature is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [contactFields, customFieldValues, formConfig, signatureDataUrl]);

  // ─── Submit Handler ────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) {
      // Scroll to first error - use a small timeout to let React update the DOM
      setTimeout(() => {
        const errorKeys = Object.keys(errors);
        const firstKey = errorKeys[0];
        if (firstKey) {
          const element = document.getElementById(firstKey);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const recaptchaToken = await executeRecaptcha('CONTACT_INTAKE');
      const response = await fetch(`${API_BASE}/public/collect/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: contactFields.firstName.trim(),
          lastName: contactFields.lastName.trim() || undefined,
          company: contactFields.company.trim() || undefined,
          phoneCountryCode: phoneCountry || undefined,
          email: contactFields.email.trim() || undefined,
          phone: contactFields.phone.trim() || undefined,
          address: contactFields.address.trim() || undefined,
          addressLine2: contactFields.addressLine2.trim() || undefined,
          city: contactFields.city.trim() || undefined,
          state: contactFields.state.trim() || undefined,
          zipCode: contactFields.zipCode.trim() || undefined,
          country: contactFields.country.trim() || undefined,
          customFields: customFieldValues,
          consent,
          signatureDataUrl,
          signatureTimestamp: new Date().toISOString(),
          website: honeypot, // Honeypot field
          prefillToken: prefillToken || undefined, // Intake-link restatement
          recaptchaToken,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to submit form');
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════
  // RENDER STATES
  // ═══════════════════════════════════════════════════════

  if (isLoading) {
    return <LoadingPage message="Loading form..." />;
  }

  if (notFound) {
    return <NotFoundPage type="form" />;
  }

  if (error && !formConfig) {
    return (
      <PortalLayout>
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md border-slate-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50"
              >
                <AlertTriangle className="h-10 w-10 text-red-500" />
              </motion.div>
              <h1 className="text-xl font-semibold text-slate-900">
                Something went wrong
              </h1>
              <p className="mt-3 text-slate-600">{error}</p>
              <Button
                variant="outline"
                className="mt-6 border-slate-300"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  if (!formConfig) {
    return <NotFoundPage type="form" />;
  }

  // ─── Dynamic Primary Color ─────────────────────────
  const primaryColor = formConfig.primaryColor || '#3b82f6';

  // ─── Success State ─────────────────────────────────
  if (isSuccess) {
    return (
      <PortalLayout showHeader={false}>
        {/* Layered gradient background for branded experience */}
        <div className="relative flex flex-1 items-center justify-center p-4 overflow-hidden">
          {/* Base gradient layer */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          {/* Radial accent - creates depth */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          {/* Subtle bottom accent */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          {/* Subtle noise texture overlay for premium feel */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          <motion.div
            variants={successVariants}
            initial="hidden"
            animate="visible"
            className="relative z-10 w-full max-w-lg"
          >
            {/* Sending company branding — keeps logo & name visible on confirmation */}
            {(formConfig.logoUrl || formConfig.companyName) && (
              <div
                className={`mb-6 ${
                  formConfig.logoPosition === 'left'
                    ? 'text-left'
                    : formConfig.logoPosition === 'right'
                    ? 'text-right'
                    : 'text-center'
                }`}
              >
                {formConfig.logoUrl && (
                  <div
                    className={`mb-3 h-12 w-auto ${
                      formConfig.logoPosition === 'center' || !formConfig.logoPosition
                        ? 'mx-auto'
                        : ''
                    }`}
                  >
                    <img
                      src={formConfig.logoUrl}
                      alt={formConfig.companyName || 'Company logo'}
                      className={`h-full w-auto object-contain ${
                        formConfig.logoPosition === 'center' || !formConfig.logoPosition
                          ? 'mx-auto'
                          : ''
                      }`}
                    />
                  </div>
                )}
                {formConfig.companyName && (
                  <p
                    className="text-sm font-medium"
                    style={{ color: primaryColor }}
                  >
                    {formConfig.companyName}
                  </p>
                )}
              </div>
            )}

            {/* Glass-morphism card */}
            <Card className="overflow-hidden border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl">
              {/* Accent bar */}
              <div
                className="h-1"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                }}
              />

              <CardContent className="p-10 text-center sm:p-12">
                {/* Success icon with animated checkmark */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <svg
                    viewBox="0 0 50 50"
                    className="h-12 w-12"
                    style={{ color: primaryColor }}
                  >
                    <motion.circle
                      cx="25"
                      cy="25"
                      r="23"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                    />
                    <motion.path
                      d="M15 26 L22 33 L35 18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      variants={checkmarkVariants}
                      initial="hidden"
                      animate="visible"
                    />
                  </svg>
                </motion.div>

                {/* Success message */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                    {formConfig.successMessage || "You're all set!"}
                  </h1>
                  <p className="mx-auto mt-4 max-w-sm text-slate-600">
                    Thank you for subscribing. We'll be in touch with updates you care about.
                  </p>
                </motion.div>

                {/* Decorative sparkles */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 flex items-center justify-center gap-2 text-sm"
                  style={{ color: primaryColor }}
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Welcome to the community</span>
                  <Sparkles className="h-4 w-4" />
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PortalLayout>
    );
  }

  // ═══════════════════════════════════════════════════════
  // MAIN FORM VIEW
  // ═══════════════════════════════════════════════════════

  const showConsent =
    formConfig.showSmsConsent || formConfig.showEmailConsent || formConfig.showVoiceConsent;

  // Drives the "you'll be opted out" notice. Submitting with nothing checked is
  // allowed — for a returning contact it is a full opt-out — so this warns
  // rather than blocks.
  const hasAnyConsent =
    consent.sms.informational ||
    consent.sms.marketing ||
    consent.email.informational ||
    consent.email.marketing ||
    consent.voice.informational ||
    consent.voice.marketing;

  return (
    <PortalLayout showHeader={false}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-1 flex-col"
      >
        {/* Layered gradient background for branded experience */}
        <div className="relative flex-1 overflow-y-auto">
          {/* Base gradient layer */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          {/* Radial accent - creates depth */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          {/* Subtle bottom accent */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          {/* Subtle noise texture overlay for premium feel */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Content */}
          <div className="relative z-10 mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
            {/* ─── Header Section ───────────────────────── */}
            <motion.div
              variants={itemVariants}
              className={`mb-8 ${
                formConfig.logoPosition === 'left'
                  ? 'text-left'
                  : formConfig.logoPosition === 'right'
                  ? 'text-right'
                  : 'text-center'
              }`}
            >
              {formConfig.logoUrl && (
                <div
                  className={`mb-6 h-16 w-auto ${
                    formConfig.logoPosition === 'center' ? 'mx-auto' : ''
                  }`}
                >
                  <img
                    src={formConfig.logoUrl}
                    alt={formConfig.companyName || 'Company logo'}
                    className={`h-full w-auto object-contain ${
                      formConfig.logoPosition === 'center' ? 'mx-auto' : ''
                    }`}
                  />
                </div>
              )}

              <h1
                className="text-2xl font-bold sm:text-3xl"
                style={{ color: primaryColor }}
              >
                {formConfig.headerText || 'Complete Your Intake Form'}
              </h1>

              {formConfig.welcomeMessage && (
                <p
                  className={`mt-3 max-w-md text-slate-600 ${
                    formConfig.logoPosition === 'center' ? 'mx-auto' : ''
                  }`}
                >
                  {formConfig.welcomeMessage}
                </p>
              )}
            </motion.div>

            {/* ─── Form Card ────────────────────────────── */}
            <motion.div variants={itemVariants}>
              {/* Glass-morphism card */}
              <Card className="overflow-hidden border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl">
                {/* Accent bar */}
                <div
                  className="h-1"
                  style={{
                    background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                  }}
                />

                <CardContent className="p-8 sm:p-10">
                  {/* Error Banner */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          <span>{error}</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* ─── Contact Fields ─────────────────── */}
                  {/* Field visibility (default all to true if not specified) */}
                  {(() => {
                    const fv = formConfig.fieldVisibility || {};
                    const showLastName = fv.lastName !== false;
                    const showCompany = fv.company !== false;
                    const showEmail = fv.email !== false;
                    const showPhone = fv.phone !== false;
                    const showAddress = fv.address !== false;
                    const showCity = fv.city !== false;
                    const showState = fv.state !== false;
                    const showZipCode = fv.zipCode !== false;
                    const showCountry = fv.country !== false;
                    const showAddressSection = showAddress || showCity || showState || showZipCode || showCountry;

                    // Input styling
                    const inputStyle: React.CSSProperties = {
                      borderRadius: formConfig.inputBorderRadius ? `${formConfig.inputBorderRadius}px` : undefined,
                      borderColor: formConfig.inputBorderColor || undefined,
                      backgroundColor: formConfig.inputBackgroundColor || undefined,
                      color: formConfig.inputTextColor || undefined,
                    };

                    return (
                  <div className="space-y-5">
                    {/* Name row */}
                    <div className={`grid grid-cols-1 gap-4 ${showLastName ? 'sm:grid-cols-2' : ''}`}>
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <User className="h-4 w-4 text-slate-400" />
                          First Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="firstName"
                          value={contactFields.firstName}
                          onChange={(e) => handleContactFieldChange('firstName', e.target.value)}
                          placeholder="John"
                          style={inputStyle}
                          className={errors.firstName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                        />
                        {errors.firstName && (
                          <p className="text-xs text-red-500">{errors.firstName}</p>
                        )}
                      </div>

                      {showLastName && (
                        <div className="space-y-2">
                          <Label htmlFor="lastName" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                            Last Name
                          </Label>
                          <Input
                            id="lastName"
                            value={contactFields.lastName}
                            onChange={(e) => handleContactFieldChange('lastName', e.target.value)}
                            placeholder="Doe"
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>

                    {/* Company */}
                    {showCompany && (
                      <div className="space-y-2">
                        <Label htmlFor="company" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          Company
                        </Label>
                        <Input
                          id="company"
                          value={contactFields.company}
                          onChange={(e) => handleContactFieldChange('company', e.target.value)}
                          placeholder="Company name"
                          style={inputStyle}
                        />
                      </div>
                    )}

                    {/* Email + Country */}
                    <div className={`grid grid-cols-1 gap-4 ${showCountry && showEmail ? 'sm:grid-cols-2' : ''}`}>
                      {showEmail && (
                        <div className="space-y-2">
                          <Label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                            <Mail className="h-4 w-4 text-slate-400" />
                            Email Address {!showPhone && <span className="text-red-500">*</span>}
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={contactFields.email}
                            onChange={(e) => handleContactFieldChange('email', e.target.value)}
                            placeholder="your@email.com"
                            style={inputStyle}
                            className={errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                          />
                          {errors.email && (
                            <p className="text-xs text-red-500">{errors.email}</p>
                          )}
                        </div>
                      )}

                      {showCountry && (
                        <div className="space-y-2">
                          <Label htmlFor="country" className="flex items-center gap-2 text-sm font-medium text-slate-700">Country</Label>
                          <select
                            id="country"
                            value={contactFields.country}
                            onChange={(e) => handleContactFieldChange('country', e.target.value)}
                            className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={inputStyle}
                          >
                            {allCountries.map((country) => (
                              <option key={country.isoCode} value={country.isoCode}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Phone */}
                    {showPhone && (
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <Phone className="h-4 w-4 text-slate-400" />
                          Phone {!showEmail && <span className="text-red-500">*</span>}
                        </Label>
                        <div className="flex min-w-0 gap-2">
                          <PhoneCountrySelect
                            value={phoneCountry}
                            onChange={(code) => {
                              setPhoneCountry(code);
                              if (errors.phone) {
                                setErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.phone;
                                  return next;
                                });
                              }
                            }}
                            borderRadius={formConfig.inputBorderRadius}
                            borderColor={formConfig.inputBorderColor}
                            inputStyle={inputStyle}
                            error={Boolean(errors.phone)}
                          />
                          <div className="flex-1 min-w-0">
                            <Input
                              id="phone"
                              type="tel"
                              inputMode="tel"
                              value={phoneLocalNumber}
                              onChange={(e) => {
                                setPhoneLocalNumber(e.target.value);
                                if (errors.phone) {
                                  setErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.phone;
                                    return next;
                                  });
                                }
                              }}
                              placeholder={phoneDialCode ? 'Local number' : 'Phone number'}
                              style={inputStyle}
                              className={errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                            />
                          </div>
                        </div>
                        {errors.phone && (
                          <p className="text-xs text-red-500">{errors.phone}</p>
                        )}
                      </div>
                    )}

                    {/* Address (collapsible or optional) */}
                    {showAddressSection && (
                      <div className="space-y-4 rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                        <Label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                          <MapPin className="h-4 w-4" />
                          Address (Optional)
                        </Label>

                        <div className="space-y-3">
                          {showAddress && (
                            <Input
                              id="address"
                              value={contactFields.address}
                              onChange={(e) => handleContactFieldChange('address', e.target.value)}
                              placeholder="Street address"
                              style={inputStyle}
                            />
                          )}

                          {showAddress && (
                            <Input
                              id="addressLine2"
                              value={contactFields.addressLine2}
                              onChange={(e) => handleContactFieldChange('addressLine2', e.target.value)}
                              placeholder="Apartment, suite, etc. (optional)"
                              style={inputStyle}
                            />
                          )}

                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {showCity && (
                              <Input
                                id="city"
                                value={contactFields.city}
                                onChange={(e) => handleContactFieldChange('city', e.target.value)}
                                placeholder="City"
                                style={inputStyle}
                              />
                            )}
                            {showState && (
                              <select
                                id="state"
                                value={contactFields.state}
                                onChange={(e) => handleContactFieldChange('state', e.target.value)}
                                className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={inputStyle}
                              >
                                <option value="">Province</option>
                                {(PROVINCE_OPTIONS[contactFields.country] ?? PROVINCE_OPTIONS['CA'] ?? []).map((province) => (
                                  <option key={province} value={province}>{province}</option>
                                ))}
                              </select>
                            )}
                            {showZipCode && (
                              <Input
                                id="zipCode"
                                value={contactFields.zipCode}
                                onChange={(e) => handleContactFieldChange('zipCode', e.target.value)}
                                placeholder="ZIP"
                                style={inputStyle}
                                className="sm:col-span-1 col-span-2"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ─── Custom Fields ─────────────────── */}
                    {formConfig.customFields && formConfig.customFields.length > 0 && (
                      <div className="space-y-4 border-t border-slate-100 pt-6">
                        <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                          Additional Information
                        </p>

                        {formConfig.customFields.map((field) => {
                          // Layout-only primitives: no label, no input, no stored value.
                          if (isLayoutFieldType(field.fieldType)) {
                            if (field.fieldType === 'section_header') {
                              return (
                                <div
                                  key={field.fieldKey}
                                  className="pt-2 pb-1 border-b border-slate-200"
                                >
                                  <h3 className="text-base font-semibold text-slate-900 tracking-tight">
                                    {field.name}
                                  </h3>
                                  {field.helpText && (
                                    <p className="text-sm text-slate-500 mt-0.5">
                                      {field.helpText}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            // spacer
                            return <div key={field.fieldKey} className="h-4" aria-hidden="true" />;
                          }

                          return (
                          <div key={field.fieldKey} className="space-y-2">
                            <Label
                              htmlFor={`custom_${field.fieldKey}`}
                              className="text-sm font-medium text-slate-700"
                            >
                              {field.name}
                              {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                            </Label>

                            {field.fieldType === 'dropdown' && field.options ? (
                              <Select
                                value={(customFieldValues[field.fieldKey] as string) || ''}
                                onValueChange={(value) => handleCustomFieldChange(field.fieldKey, value)}
                              >
                                <SelectTrigger
                                  id={`custom_${field.fieldKey}`}
                                  className={errors[`custom_${field.fieldKey}`] ? 'border-red-300' : ''}
                                >
                                  <SelectValue placeholder={field.placeholder || 'Select...'} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.options.map((opt) => (
                                    <SelectItem key={opt} value={opt}>
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : field.fieldType === 'radio' && field.options && field.options.length > 0 ? (
                              <div role="radiogroup" aria-label={field.name} className="space-y-2">
                                {field.options.map((opt) => (
                                  <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`custom_${field.fieldKey}`}
                                      value={opt}
                                      checked={(customFieldValues[field.fieldKey] as string) === opt}
                                      onChange={() => handleCustomFieldChange(field.fieldKey, opt)}
                                      className="accent-blue-600"
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            ) : field.fieldType === 'checkboxes' && field.options && field.options.length > 0 ? (
                              <div role="group" aria-label={field.name} className="space-y-2">
                                {field.options.map((opt) => {
                                  const selected = (customFieldValues[field.fieldKey] as string[]) || [];
                                  const isChecked = Array.isArray(selected) && selected.includes(opt);
                                  return (
                                    <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        value={opt}
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const current = Array.isArray(customFieldValues[field.fieldKey])
                                            ? (customFieldValues[field.fieldKey] as string[])
                                            : [];
                                          const next = e.target.checked
                                            ? [...current, opt]
                                            : current.filter((v) => v !== opt);
                                          handleCustomFieldChange(field.fieldKey, next);
                                        }}
                                        className="accent-blue-600"
                                      />
                                      {opt}
                                    </label>
                                  );
                                })}
                              </div>
                            ) : field.fieldType === 'checkboxes' ? (
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  id={`custom_${field.fieldKey}`}
                                  checked={!!customFieldValues[field.fieldKey]}
                                  onCheckedChange={(checked) =>
                                    handleCustomFieldChange(field.fieldKey, !!checked)
                                  }
                                />
                              </div>
                            ) : field.fieldType === 'date' ? (
                              <Input
                                id={`custom_${field.fieldKey}`}
                                type="date"
                                value={(customFieldValues[field.fieldKey] as string) || ''}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                className={errors[`custom_${field.fieldKey}`] ? 'border-red-300' : ''}
                              />
                            ) : field.fieldType === 'textarea' ? (
                              <textarea
                                id={`custom_${field.fieldKey}`}
                                value={(customFieldValues[field.fieldKey] as string) || ''}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={field.placeholder || ''}
                                className={`w-full rounded-md border px-3 py-2 text-sm ${
                                  errors[`custom_${field.fieldKey}`]
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                                rows={3}
                              />
                            ) : field.fieldType === 'image' ? (
                              <ImageUploadField
                                fieldKey={field.fieldKey}
                                value={(customFieldValues[field.fieldKey] as string) || ''}
                                placeholder={field.placeholder || ''}
                                onChange={(val) => handleCustomFieldChange(field.fieldKey, val)}
                                error={errors[`custom_${field.fieldKey}`]}
                              />
                            ) : (
                              <Input
                                id={`custom_${field.fieldKey}`}
                                type={field.fieldType === 'number' ? 'number' : 'text'}
                                value={(customFieldValues[field.fieldKey] as string) || ''}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={field.placeholder || ''}
                                className={errors[`custom_${field.fieldKey}`] ? 'border-red-300' : ''}
                              />
                            )}

                            {field.helpText && (
                              <p className="text-xs text-slate-500">{field.helpText}</p>
                            )}

                            {errors[`custom_${field.fieldKey}`] && (
                              <p className="text-xs text-red-500">{errors[`custom_${field.fieldKey}`]}</p>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ─── Consent Section ───────────────── */}
                    {showConsent && (
                      <div id="consentEmailInfo" className="space-y-4 border-t border-slate-100 pt-6">
                        <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                          Communication Preferences
                        </p>
                        <p className="text-xs text-slate-600">
                          Select how you&apos;d like to receive communications. You can opt-in to service notifications, marketing messages, or both.
                        </p>
                        <p className="text-xs text-slate-600">
                          Your selections below replace any preferences you&apos;ve given us before &mdash; anything you leave unchecked will be turned off.
                        </p>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <h3 className="text-sm font-bold text-slate-900">Service Notifications</h3>
                            <p className="mt-1 text-xs text-slate-600">
                              Essential communications including signatures, updates, and security alerts.
                            </p>
                            <div className="mt-3 space-y-2 text-xs text-slate-800">
                              {formConfig.showEmailConsent && formConfig.emailShowInformational && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.email.informational}
                                    onCheckedChange={(checked) => handleConsentChange('email', 'informational', !!checked)}
                                  />
                                  <span>Email</span>
                                </label>
                              )}
                              {formConfig.showSmsConsent && formConfig.smsShowInformational && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.sms.informational}
                                    onCheckedChange={(checked) => handleConsentChange('sms', 'informational', !!checked)}
                                  />
                                  <span>SMS/Text Messages</span>
                                </label>
                              )}
                              {formConfig.showVoiceConsent && formConfig.voiceShowInformational && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.voice.informational}
                                    onCheckedChange={(checked) => handleConsentChange('voice', 'informational', !!checked)}
                                  />
                                  <span>Voice Calls</span>
                                </label>
                              )}
                            </div>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <h3 className="text-sm font-bold text-slate-900">Marketing Messages</h3>
                            <p className="mt-1 text-xs text-slate-600">
                              Optional communications about promotions, features, and announcements.
                            </p>
                            <div className="mt-3 space-y-2 text-xs text-slate-800">
                              {formConfig.showEmailConsent && formConfig.emailShowMarketing && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.email.marketing}
                                    onCheckedChange={(checked) => handleConsentChange('email', 'marketing', !!checked)}
                                  />
                                  <span>Email</span>
                                </label>
                              )}
                              {formConfig.showSmsConsent && formConfig.smsShowMarketing && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.sms.marketing}
                                    onCheckedChange={(checked) => handleConsentChange('sms', 'marketing', !!checked)}
                                  />
                                  <span>SMS/Text Messages</span>
                                </label>
                              )}
                              {formConfig.showVoiceConsent && formConfig.voiceShowMarketing && (
                                <label className="flex items-center gap-2">
                                  <Checkbox
                                    checked={consent.voice.marketing}
                                    onCheckedChange={(checked) => handleConsentChange('voice', 'marketing', !!checked)}
                                  />
                                  <span>Voice Calls</span>
                                </label>
                              )}
                            </div>
                          </div>
                        </div>

                        {consentTouched && !hasAnyConsent && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs text-amber-900">
                              You haven&apos;t selected any communication preferences. If you previously opted in, submitting this form will opt you out of every option shown above.
                            </p>
                          </div>
                        )}

                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                          <p className="text-xs text-slate-700">
                            <span className="font-semibold">SMS/Text Message Disclosure:</span> By checking any SMS/Text option above, you consent to receive text messages. Messaging frequency varies. Msg & Data rates may apply.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ─── Honeypot Field (hidden) ─────── */}
                    <div className="sr-only" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        name="website"
                        type="text"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* ─── Submit Button ─────────────────── */}
                    <div id="signature" className="space-y-2 border-t border-slate-100 pt-6">
                      <p className="text-xs text-slate-600">
                        By signing below, you acknowledge and agree to the communication preferences selected above.
                      </p>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-slate-700">
                          Signature <span className="text-red-500">*</span>
                        </Label>
                        <button
                          type="button"
                          onClick={clearSignature}
                          className="text-xs text-slate-500 hover:text-slate-700 underline"
                        >
                          Clear
                        </button>
                      </div>
                      <canvas
                        ref={signatureCanvasRef}
                        className="w-full h-[140px] rounded-lg border border-slate-200 bg-white touch-none"
                        onPointerDown={handleSignaturePointerDown}
                        onPointerMove={handleSignaturePointerMove}
                        onPointerUp={finalizeSignature}
                        onPointerLeave={finalizeSignature}
                      />
                      {errors.signature && (
                        <p className="text-xs text-red-500">{errors.signature}</p>
                      )}
                      <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                        <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                        <p className="mt-1">Your IP address will be recorded with this signature for compliance purposes.</p>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        className="w-full py-3 text-base font-medium transition-all"
                        style={{
                          backgroundColor: primaryColor,
                          borderColor: primaryColor,
                        }}
                        size="lg"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-5 w-5" />
                            Submit Intake
                          </>
                        )}
                      </Button>

                      {/* Privacy note */}
                      <p className="mt-4 text-center text-xs text-slate-400">
                        Your information is secure and encrypted. We'll never share your data.
                      </p>
                    </div>
                  </div>
                    );
                  })()}

                  {/* ─── Footer ───────────────────────────── */}
                  {(() => {
                    const footerEnabled = formConfig.footerConfig?.enabled !== false;
                    const hasFooterText = footerEnabled && !!formConfig.footerConfig?.text;
                    const hasFooterLinks =
                      footerEnabled &&
                      !!formConfig.footerConfig?.links &&
                      formConfig.footerConfig.links.length > 0;
                    const hasBusinessInfo =
                      !!formConfig.companyName || !!formConfig.companyAddress;
                    if (!hasFooterText && !hasFooterLinks && !hasBusinessInfo) return null;
                    return (
                    <div className="text-center text-xs text-slate-500 mt-6 pt-4 border-t border-slate-100">
                      {hasFooterText && (
                        <p>{formConfig.footerConfig?.text}</p>
                      )}
                      {formConfig.companyName && (
                        <p className="mt-1 font-medium text-slate-600">{formConfig.companyName}</p>
                      )}
                      {formConfig.companyAddress && (
                        <p className="mt-1">{formConfig.companyAddress}</p>
                      )}
                      {hasFooterLinks && (
                        <div className="flex justify-center gap-4 mt-2 flex-wrap">
                          {formConfig.footerConfig!.links!.map((link, idx) => (
                            link.label && link.url && (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-slate-700 underline"
                              >
                                {link.label}
                              </a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </PortalLayout>
  );
}

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

export default CollectContact;
